// client/src/components/Banner.js
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Container, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useStore } from '../context/StoreContext';
import { API_BASE_URL, getImageUrl } from '../utils/media';

const ROTATE_INTERVAL_MS = 5000;
const BANNER_HEIGHT = { xs: 320, sm: 420, md: 520 };

// A banner slide's link_url can be an internal path (e.g. "/products?category=Sale")
// or a full external URL - internal paths use client-side routing (no full
// page reload), external ones are a plain anchor tag.
const SlideLink = ({ linkUrl, children }) => {
  if (!linkUrl) return children;
  const isExternal = /^https?:\/\//i.test(linkUrl);
  if (isExternal) {
    return <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>{children}</a>;
  }
  return <RouterLink to={linkUrl} style={{ display: 'block' }}>{children}</RouterLink>;
};

const Banner = () => {
  const { store } = useStore();
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!store.id) return;
    fetch(`${API_BASE_URL}/api/store/banners`)
      .then((res) => (res.ok ? res.json() : []))
      .then(setSlides)
      .catch(() => setSlides([]));
  }, [store.id]);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [slides.length]);

  const goTo = useCallback((index) => {
    setCurrent((index + slides.length) % slides.length);
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          py: { xs: 8, md: 12 },
          mb: 5,
          borderRadius: { xs: 0, md: 4 },
          background:
            'radial-gradient(900px 500px at 10% 20%, rgba(255,255,255,0.22), transparent 60%),' +
            'radial-gradient(700px 500px at 90% 80%, rgba(255,160,90,0.45), transparent 60%),' +
            'linear-gradient(135deg, #3563E9 0%, #1F3FA8 60%, #142a7a 100%)',
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative' }}>
          <Typography
            variant="overline"
            sx={{ color: 'rgba(255,255,255,0.75)', letterSpacing: '0.18em', fontWeight: 700 }}
          >
            New season
          </Typography>
          <Typography
            variant="h2"
            fontWeight={800}
            sx={{ fontSize: { xs: '2.4rem', md: '3.75rem' }, lineHeight: 1.05, maxWidth: 720, mb: 2 }}
          >
            Everyday essentials, reimagined.
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, fontWeight: 400, maxWidth: 560 }}>
            Shop our latest collection, and bid on live auctions as they happen.
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: BANNER_HEIGHT,
        mb: 4,
        borderRadius: { xs: 0, md: 3 },
        overflow: 'hidden',
      }}
    >
      {slides.map((slide, index) => (
        <Box
          key={slide.id}
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: index === current ? 1 : 0,
            transition: 'opacity 0.6s ease-in-out',
            pointerEvents: index === current ? 'auto' : 'none',
          }}
        >
          <SlideLink linkUrl={slide.link_url}>
            <Box
              component="img"
              src={getImageUrl(slide.image_url)}
              alt={`${store.name} banner ${index + 1}`}
              sx={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
            />
          </SlideLink>
        </Box>
      ))}

      {slides.length > 1 && (
        <>
          <IconButton
            onClick={() => goTo(current - 1)}
            aria-label="Previous banner"
            sx={{
              position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.4)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={() => goTo(current + 1)}
            aria-label="Next banner"
            sx={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.4)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>

          <Box sx={{ position: 'absolute', bottom: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 1 }}>
            {slides.map((slide, index) => (
              <Box
                key={slide.id}
                component="button"
                onClick={() => goTo(index)}
                aria-label={`Go to banner ${index + 1}`}
                sx={{
                  width: 9, height: 9, borderRadius: '50%', border: 'none', p: 0, cursor: 'pointer',
                  bgcolor: index === current ? '#fff' : 'rgba(255,255,255,0.5)',
                }}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default Banner;
